package org.example.project.enginepersonal

import org.example.project.settings.UISettings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import kotlinx.browser.window
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date

private val stempelJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun stempelApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

typealias StempelScanResult = All
typealias StempelActionResult = All

private object StempeluhrApi {
    private fun firmenId(): String =
        LoginService.getUser()?.firmenId.orEmpty().trim()
            .ifBlank { GlobalManager.currentFirmenId.trim() }

    suspend fun scan(qrCode: String): StempelScanResult {
        val raw = runCatching {
            LumaticaApiManager.call("zeiterfassung/scan-json/${stempelApiPart(firmenId())}/${stempelApiPart(qrCode)}")
        }.getOrDefault("")
        return runCatching { stempelJson.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrDefault(StempelScanResult(error = "QR Code konnte nicht gelesen werden."))
    }

    suspend fun stamp(
        qrCode: String,
        action: String,
        bereich: String,
        terminalId: String
    ): StempelActionResult {
        val body = buildJsonObject {
            put("qrCode", JsonPrimitive(qrCode))
            put("aktion", JsonPrimitive(action))
            put("bereich", JsonPrimitive(bereich))
            put("terminalId", JsonPrimitive(terminalId))
        }.toString()
        val raw = runCatching {
            LumaticaApiManager.call("zeiterfassung/stempel-json/${stempelApiPart(firmenId())}/${stempelApiPart(body)}")
        }.getOrDefault("")
        val result = runCatching { stempelJson.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrDefault(StempelActionResult(error = "Stempelung konnte nicht gespeichert werden."))
        if (result.ok) {
            markStempelTagChanged(result)
        }
        return result
    }

    suspend fun mobileStatus(): StempelActionResult {
        val raw = runCatching {
            LumaticaApiManager.call("zeiterfassung/mobile-status-json")
        }.getOrDefault("")
        return runCatching { stempelJson.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrDefault(StempelActionResult(error = "Arbeitszeiterfassung konnte nicht geladen werden."))
    }

    suspend fun mobileStamp(action: String): StempelActionResult {
        val body = buildJsonObject { put("aktion", JsonPrimitive(action)) }.toString()
        val raw = runCatching {
            LumaticaApiManager.postJson("zeiterfassung/mobile-stempel-json", body)
        }.getOrDefault("")
        val result = runCatching { stempelJson.decodeFromString(All.AllNames.serializer(), raw) }
            .getOrDefault(StempelActionResult(error = "Stempelung konnte nicht gespeichert werden."))
        if (result.ok) markStempelTagChanged(result)
        return result
    }

    private fun markStempelTagChanged(result: StempelActionResult) {
        val tag = result.tag
        val userId = tag?.userId.orEmpty().ifBlank { result.person?.userId.orEmpty() }.trim()
        val datum = tag?.datum.orEmpty().trim()
        if (userId.isBlank() || !Regex("""^\d{4}-\d{2}-\d{2}$""").matches(datum)) {
            GlobalManager.markJsonChanged("zeit", "zeiterfassung/stempelung.json")
            return
        }
        GlobalManager.markJsonChanged(
            "zeit",
            "user/$userId/zeiterfassung/${datum.take(4)}/${datum.substring(5, 7)}/${datum.substring(8, 10)}.json"
        )
    }
}

@Composable
fun StempeluhrScreen(mobileArbeitszeiterfassung: Boolean = false) {
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("zeit"),
        GlobalManager.jsonDomainVersion("user"),
        GlobalManager.jsonDomainVersion("personal")
    )
    var clock by remember { mutableStateOf(stempelClock()) }
    var qrInput by remember { mutableStateOf("") }
    var dateLine by remember { mutableStateOf(stempelDateLine()) }
    var qrCode by remember { mutableStateOf("") }
    var person by remember { mutableStateOf<All?>(null) }
    var bereich by remember { mutableStateOf("") }
    var loading by remember { mutableStateOf(mobileArbeitszeiterfassung) }
    var message by remember { mutableStateOf("") }
    var successMessage by remember { mutableStateOf("") }
    var scannerKey by remember { mutableStateOf(0) }
    var terminal by remember { mutableStateOf<StempeluhrProfil?>(null) }
    var firmaFallbackStandort by remember { mutableStateOf("") }
    var showTerminalSettings by remember { mutableStateOf(false) }
    var settingsPin by remember { mutableStateOf("") }
    var settingsSetupToken by remember { mutableStateOf("") }
    var settingsUnlocked by remember { mutableStateOf(false) }
    var settingsMessage by remember { mutableStateOf("") }
    var unlockedTerminals by remember { mutableStateOf<List<StempeluhrProfil>>(emptyList()) }
    var selectedTerminalId by remember { mutableStateOf("") }
    var clockTapCount by remember { mutableStateOf(0) }
    var clockTapStartedAt by remember { mutableStateOf(0.0) }
    var currentStatus by remember { mutableStateOf("") }
    var currentBereich by remember { mutableStateOf("") }
    val scope = rememberCoroutineScope()

    fun terminalDisplay(profile: StempeluhrProfil): String {
        val profileDisplay = listOf(
            profile.standortAnzeige.ifBlank { profile.standort },
            profile.abteilung
        )
            .filter { it.isNotBlank() }
            .joinToString(" - ")
        return profileDisplay.ifBlank { firmaFallbackStandort }
    }

    fun unlockTerminalSelection() {
        if (settingsPin.isBlank()) {
            settingsMessage = "PIN eingeben."
            return
        }
        scope.launch {
            loading = true
            settingsMessage = ""
            val result = StempeluhrProfilApi.unlock(settingsPin)
            loading = false
            if (result.ok) {
                unlockedTerminals = result.terminals
                settingsUnlocked = true
                settingsSetupToken = result.setupToken
                selectedTerminalId = selectedTerminalId.ifBlank { result.terminals.firstOrNull()?.id.orEmpty() }
                settingsPin = ""
            } else {
                settingsSetupToken = ""
                settingsMessage = stempeluhrProfileError(result.error)
            }
        }
    }

    fun activateSelectedTerminal() {
        if (selectedTerminalId.isBlank() || settingsSetupToken.isBlank()) {
            settingsMessage = "Stempeluhr auswählen."
            return
        }
        scope.launch {
            loading = true
            settingsMessage = ""
            val result = StempeluhrProfilApi.activate(selectedTerminalId, settingsSetupToken)
            loading = false
            if (result.ok && result.token.isNotBlank()) {
                terminal = result.terminal
                window.localStorage.setItem("lumatica.stempeluhr.token", result.token)
                window.localStorage.removeItem("lumatica.stempeluhr.id")
                unlockedTerminals = emptyList()
                settingsUnlocked = false
                settingsSetupToken = ""
                selectedTerminalId = ""
                showTerminalSettings = false
                scannerKey++
            } else {
                settingsMessage = stempeluhrProfileError(result.error)
            }
        }
    }

    LaunchedEffect(Unit) {
        if (mobileArbeitszeiterfassung) return@LaunchedEffect
        if (!LoginService.isLoggedIn()) return@LaunchedEffect
        firmaFallbackStandort = runCatching {
            KarteiApi.loadRegisterFirma()?.let(::stempeluhrFirmaStandortFallback).orEmpty()
        }.getOrDefault("")
    }

    LaunchedEffect(refreshVersion, mobileArbeitszeiterfassung) {
        if (mobileArbeitszeiterfassung) {
            loading = true
            val result = StempeluhrApi.mobileStatus()
            loading = false
            if (result.ok) {
                person = All(id = LoginService.getId())
                currentStatus = result.tag?.status.orEmpty()
                currentBereich = ""
            } else {
                message = stempelError(result.error)
            }
            return@LaunchedEffect
        }
        val savedToken = window.localStorage.getItem("lumatica.stempeluhr.token").orEmpty()
        if (savedToken.isNotBlank()) {
            loading = true
            val result = StempeluhrProfilApi.loadActivated(savedToken)
            loading = false
            if (result.ok) {
                terminal = result.terminal
                showTerminalSettings = false
                scannerKey++
            } else {
                window.localStorage.removeItem("lumatica.stempeluhr.token")
                window.localStorage.removeItem("lumatica.stempeluhr.id")
                selectedTerminalId = ""
                showTerminalSettings = true
            }
        } else {
            window.localStorage.removeItem("lumatica.stempeluhr.id")
            showTerminalSettings = true
        }
    }

    LaunchedEffect(Unit) {
        while (true) {
            clock = stempelClock()
            dateLine = stempelDateLine()
            delay(1000)
        }
    }

    LaunchedEffect(successMessage) {
        if (successMessage.isNotBlank()) {
            delay(350)
            successMessage = ""
            if (mobileArbeitszeiterfassung) mobileArbeitszeitZurueckZumDashboard() else scannerKey++
        }
    }

    DisposableEffect(Unit) {
        onDispose { stopCameraQr() }
    }

    fun resetToScanner() {
        stopCameraQr()
        qrInput = ""
        qrCode = ""
        person = null
        bereich = ""
        currentStatus = ""
        currentBereich = ""
        message = ""
        scannerKey++
    }

    fun scanQr(rawCode: String) {
        val code = rawCode.trim()
        if (code.isBlank()) {
            scannerKey++
            return
        }
        scope.launch {
            loading = true
            message = ""
            val result = StempeluhrApi.scan(code)
            loading = false
            if (result.ok) {
                qrCode = code
                person = result.person
                currentStatus = result.tag?.status.orEmpty()
                currentBereich = result.tag?.bereich.orEmpty()
                bereich = terminal?.abteilung.orEmpty().ifBlank { result.tag?.bereich.orEmpty() }
                stopCameraQr()
                val activeTerminal = terminal
                if (
                    activeTerminal?.direktWechsel == true &&
                    result.tag?.status == "eingestempelt" &&
                    activeTerminal.abteilung.isNotBlank() &&
                    result.tag?.bereich != activeTerminal.abteilung
                ) {
                    loading = true
                    val switched = StempeluhrApi.stamp(
                        code,
                        "bereichwechsel",
                        activeTerminal.abteilung,
                        activeTerminal.id
                    )
                    loading = false
                    if (switched.ok) {
                        successMessage = "Wechsel"
                        qrInput = ""
                        qrCode = ""
                        person = null
                        bereich = ""
                        currentStatus = ""
                        currentBereich = ""
                    } else {
                        message = stempelError(switched.error)
                    }
                }
            } else {
                message = stempelError(result.error)
                delay(900)
                message = ""
                scannerKey++
            }
        }
    }

    fun stamp(action: String) {
        if (mobileArbeitszeiterfassung) {
            scope.launch {
                loading = true
                message = ""
                val result = StempeluhrApi.mobileStamp(
                    if (action == "einstempeln") "kommen" else "gehen"
                )
                loading = false
                if (result.ok) {
                    successMessage = if (action == "einstempeln") "Kommen" else "Gehen"
                } else {
                    message = stempelError(result.error)
                }
            }
            return
        }
        val code = qrCode.trim()
        if (code.isBlank()) {
            resetToScanner()
            return
        }
        if (action == "bereichwechsel" && bereich.trim().isBlank()) {
            message = "Abteilung fehlt."
            return
        }
        scope.launch {
            loading = true
            message = ""
            val result = StempeluhrApi.stamp(
                code,
                action,
                bereich.trim(),
                terminal?.id.orEmpty()
            )
            loading = false
            if (result.ok) {
                successMessage = when (action) {
                    "einstempeln" -> "Kommen"
                    "ausstempeln" -> "Gehen"
                    else -> "Wechsel"
                }
                qrInput = ""
                qrCode = ""
                person = null
                bereich = ""
                currentStatus = ""
                currentBereich = ""
            } else {
                message = stempelError(result.error)
            }
        }
    }

    LaunchedEffect(scannerKey, person, successMessage, terminal, showTerminalSettings, mobileArbeitszeiterfassung) {
        if (mobileArbeitszeiterfassung) return@LaunchedEffect
        if (person == null && successMessage.isBlank() && terminal != null && !showTerminalSettings) {
            delay(150)
            requestCameraQr(
                onResult = { code ->
                    qrInput = code
                    scanQr(code)
                },
                onError = { error -> message = error }
            )
        }
    }

    Style(StempeluhrStyles)
    Div({
        classes(StempeluhrStyles.page)
        if (mobileArbeitszeiterfassung) style {
            backgroundColor(Color.transparent)
        }
    }) {
        Div({ classes(StempeluhrStyles.shell) }) {
                Div({
                    classes(StempeluhrStyles.clockCard)
                    if (mobileArbeitszeiterfassung) style {
                        backgroundColor(Color.transparent)
                    }
                    onClick {
                        if (mobileArbeitszeiterfassung) return@onClick
                        val now = Date().getTime()
                        val next = if (now - clockTapStartedAt > 5000.0) 1 else clockTapCount + 1
                        clockTapStartedAt = if (next == 1) now else clockTapStartedAt
                        clockTapCount = next
                        if (next >= 10) {
                            clockTapCount = 0
                            settingsPin = ""
                            settingsMessage = ""
                            unlockedTerminals = emptyList()
                            settingsUnlocked = false
                            settingsSetupToken = ""
                            selectedTerminalId = ""
                            showTerminalSettings = true
                            stopCameraQr()
                        }
                    }
                }) {
                    if (successMessage.isNotBlank()) {
                        Div({ classes(StempeluhrStyles.clockTime) }) { Text("OK") }
                    } else {
                        Div({ classes(StempeluhrStyles.clockTime) }) { Text(clock) }
                        Div({ classes(StempeluhrStyles.clockDate) }) { Text(dateLine) }
                        if (!mobileArbeitszeiterfassung) terminal?.let { profile ->
                            Div({ classes(StempeluhrStyles.terminalMeta) }) {
                                Text(terminalDisplay(profile))
                            }
                        }
                    }
                }

                if (!mobileArbeitszeiterfassung && person == null) {
                    Div({
                        classes(StempeluhrStyles.cameraPreview)
                        attr("id", "stempeluhr-camera-preview")
                    }) {
                        Text("QR vor Gerät halten")
                    }
                } else if (person != null) {
                    Div({ classes(StempeluhrStyles.actions) }) {
                        if (currentStatus == "eingestempelt") {
                            StempelActionButton("Gehen", enabled = !loading) { stamp("ausstempeln") }
                        } else {
                            StempelActionButton("Kommen", enabled = !loading) { stamp("einstempeln") }
                        }
                        if (!mobileArbeitszeiterfassung) LumaticaButton(
                            label = "Abbrechen",
                            onClick = { resetToScanner() },
                            variant = LumaticaButtonVariant.Standard,
                            enabled = !loading,
                            heightPx = 42
                        )
                    }
                    if (!mobileArbeitszeiterfassung &&
                        currentStatus == "eingestempelt" &&
                        terminal?.direktWechsel != true &&
                        terminal?.abteilung.orEmpty().isNotBlank() &&
                        currentBereich != terminal?.abteilung
                    ) {
                        Div({ classes(StempeluhrStyles.areaBox) }) {
                            AppDropdown(
                                options = terminal?.abteilungen.orEmpty().map { All(key = it, label = it) },
                                selectedValue = bereich.ifBlank { null },
                                onChange = { bereich = it },
                                placeholder = "Abteilung wählen",
                                appearance = AppDropdownAppearance.Surface
                            )
                        LumaticaButton(
                            label = "Abteilung wechseln",
                            onClick = { if (!loading) stamp("bereichwechsel") },
                            variant = LumaticaButtonVariant.Action,
                            enabled = !loading,
                            heightPx = 42
                        )
                        }
                    }
                }

                if (message.isNotBlank() || loading) {
                    Div({ classes(StempeluhrStyles.message) }) {
                        Text(if (loading) "Speichere..." else message)
                    }
                }
            }
        if (!mobileArbeitszeiterfassung && showTerminalSettings) {
            Div({ classes(StempeluhrStyles.settingsOverlay) }) {
                Div({ classes(StempeluhrStyles.settingsBox) }) {
                    Div({ classes(StempeluhrStyles.settingsTitle) }) { Text("Stempeluhr aktivieren") }
                    if (!settingsUnlocked) {
                        Input(InputType.Password, attrs = {
                            classes(StempeluhrStyles.settingsInput)
                            value(settingsPin)
                            placeholder("PIN")
                            onInput { settingsPin = it.value }
                            onKeyDown { if (it.key == "Enter") unlockTerminalSelection() }
                        })
                    } else {
                        AppDropdown(
                            options = unlockedTerminals.map { profile ->
                                All(
                                    key = profile.id,
                                    label = listOf(
                                        profile.standortAnzeige.ifBlank { profile.standort },
                                        profile.abteilung
                                    ).filter { it.isNotBlank() }.joinToString(" - ")
                                )
                            },
                            selectedValue = selectedTerminalId.ifBlank { null },
                            onChange = { selectedTerminalId = it },
                            placeholder = "Stempeluhr auswählen",
                            appearance = AppDropdownAppearance.Surface
                        )
                    }
                    if (settingsMessage.isNotBlank()) {
                        Div({ classes(StempeluhrStyles.message) }) { Text(settingsMessage) }
                    }
                    Div({ classes(StempeluhrStyles.settingsActions) }) {
                        if (terminal != null) {
                            StempelActionButton("Abbrechen", enabled = !loading) {
                                settingsPin = ""
                                settingsSetupToken = ""
                                settingsUnlocked = false
                                unlockedTerminals = emptyList()
                                showTerminalSettings = false
                                scannerKey++
                            }
                        }
                        if (!settingsUnlocked) {
                            StempelActionButton("Weiter", enabled = !loading) { unlockTerminalSelection() }
                        } else {
                            StempelActionButton("Starten", enabled = !loading) { activateSelectedTerminal() }
                        }
                    }
                }
            }
        }
    }
}

@Composable
internal fun StempelActionButton(label: String, enabled: Boolean, onClick: () -> Unit) {
    LumaticaButton(
        label = label,
        onClick = { if (enabled) onClick() },
        variant = LumaticaButtonVariant.Action,
        enabled = enabled,
        heightPx = 42
    )
}

private fun requestCameraQr(onResult: (String) -> Unit, onError: (String) -> Unit) {
    val start = js(
        """
        (function(onResult, onError) {
            if (window.__lumaticaStempelStopCamera) window.__lumaticaStempelStopCamera();
            var preview = document.getElementById("stempeluhr-camera-preview");
            if (!window.isSecureContext && window.location.hostname !== "localhost" && window.location.hostname !== "127.0.0.1") {
                onError("Kamera braucht HTTPS. Bitte die mobile Seite über https://" + window.location.hostname + "/mobile öffnen.");
                return;
            }
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                onError("Kamera ist blockiert. Bitte Chrome/Safari über HTTPS nutzen und Kamerazugriff erlauben.");
                return;
            }
            if (typeof BarcodeDetector === "undefined") {
                navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } })
                    .then(function(stream) {
                        stream.getTracks().forEach(function(track) { track.stop(); });
                        onError("Kamera freigegeben, aber QR-Erkennung fehlt. Bitte Chrome auf Android oder ein unterstütztes Gerät nutzen.");
                    })
                    .catch(function() { onError("Kamera wurde nicht freigegeben."); });
                return;
            }
            navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } })
                .then(function(stream) {
                    var video = document.createElement("video");
                    video.setAttribute("playsinline", "true");
                    video.muted = true;
                    video.srcObject = stream;
                    video.style.width = "1px";
                    video.style.height = "1px";
                    video.style.opacity = "0";
                    video.style.position = "absolute";
                    video.style.pointerEvents = "none";
                    if (preview) {
                        preview.innerHTML = "";
                        preview.appendChild(video);
                        var label = document.createElement("div");
                        label.textContent = "QR vor Gerät halten";
                        label.style.width = "100%";
                        label.style.height = "100%";
                        label.style.display = "flex";
                        label.style.alignItems = "center";
                        label.style.justifyContent = "center";
                        label.style.textAlign = "center";
                        preview.appendChild(label);
                    }
                    var detector = new BarcodeDetector({ formats: ["qr_code"] });
                    var stopped = false;
                    function stop() {
                        if (stopped) return;
                        stopped = true;
                        stream.getTracks().forEach(function(track) { track.stop(); });
                        window.__lumaticaStempelStopCamera = null;
                    }
                    window.__lumaticaStempelStopCamera = stop;
                    function scan() {
                        if (stopped) return;
                        detector.detect(video)
                            .then(function(codes) {
                                if (codes && codes.length && codes[0].rawValue) {
                                    stop();
                                    onResult(codes[0].rawValue);
                                } else {
                                    setTimeout(scan, 250);
                                }
                            })
                            .catch(function() {
                                stop();
                                onError("QR Code konnte nicht gelesen werden.");
                            });
                    }
                    video.play()
                        .then(function() { scan(); })
                        .catch(function() {
                            stop();
                            onError("Kamera konnte nicht gestartet werden.");
                        });
                })
                .catch(function() { onError("Kamera wurde nicht freigegeben."); });
        })
        """
    )
    start(onResult, onError)
}

private fun stopCameraQr() {
    js(
        """
        (function() {
            if (window.__lumaticaStempelStopCamera) {
                window.__lumaticaStempelStopCamera();
                window.__lumaticaStempelStopCamera = null;
            }
        })()
        """
    )
}

internal fun stempelClock(): String {
    val now = Date()
    return listOf(now.getHours(), now.getMinutes(), now.getSeconds())
        .joinToString(":") { it.toInt().toString().padStart(2, '0') }
}

internal fun stempelDateLine(): String {
    val now = Date()
    val weekdays = listOf("Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag")
    val weekday = weekdays.getOrElse(now.getDay().toInt()) { "" }
    val day = now.getDate().toInt().toString().padStart(2, '0')
    val month = (now.getMonth().toInt() + 1).toString().padStart(2, '0')
    val year = now.getFullYear().toInt().toString()
    return "$weekday, $day.$month.$year"
}

private fun mobileArbeitszeitZurueckZumDashboard() {
    window.postMessage(js("({ type: 'lumatica-focus-close' })"), "*")
}

private fun stempeluhrFirmaStandortFallback(firma: All): String {
    val name = firma.firma.ifBlank {
        firma.kunde.ifBlank { firma.name.ifBlank { firma.bezeichnung } }
    }
    val address = listOf(firma.strasse, firma.hausnummer)
        .filter { it.isNotBlank() }
        .joinToString(" ")
    return listOf(name, address)
        .filter { it.isNotBlank() }
        .joinToString(" - ")
}

private fun stempelError(error: String): String =
    when (error) {
        "zeiterfassung_qr_unvollstaendig" -> "QR Code ist unvollständig."
        "zeiterfassung_user_nicht_gefunden" -> "Mitarbeiter wurde nicht gefunden."
        "zeiterfassung_qr_passt_nicht_zum_user" -> "QR Code passt nicht zum gespeicherten Mitarbeiter."
        "zeiterfassung_aktion_unbekannt" -> "Stempelaktion ist ungültig."
        else -> error.ifBlank { "Aktion fehlgeschlagen." }
    }

internal object StempeluhrStyles : StyleSheet() {
    val page by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        boxSizing("border-box")
        overflow("hidden")
        color(Color.white)
        backgroundColor(Color("#050608"))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
    }
    val shell by style {
        width(100.percent)
        maxWidth(520.px)
        property("margin", "0 auto")
        padding(18.px)
        boxSizing("border-box")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Stretch)
        gap(14.px)
    }
    val clockCard by style {
        width(100.percent)
        minHeight(150.px)
        borderRadius(8.px)
        property("border", "2px solid rgba(255,255,255,0.82)")
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(10.px)
        backgroundColor(Color("#07090d"))
        color(Color.white)
        property("box-sizing", "border-box")
    }
    val clockTime by style {
        color(Color.white)
        fontWeight(800)
        fontSize(58.px)
        lineHeight("1")
        letterSpacing(0.px)
    }
    val clockDate by style {
        color(Color("#c8ced8"))
        fontWeight(800)
        fontSize(18.px)
        lineHeight("1.2")
    }
    val terminalMeta by style {
        color(Color("#84e1a7"))
        fontWeight(800)
        fontSize(13.px)
        textAlign("center")
    }
    val areaBox by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        color(Color("#d7dbe5"))
        fontSize(13.px)
        fontWeight(700)
    }
    val areaInput by style {
        width(100.percent)
        height(54.px)
        borderRadius(8.px)
        padding(0.px, 14.px)
        backgroundColor(Color("#090b10"))
        color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("box-sizing", "border-box")
        fontSize(18.px)
    }
    val actions by style {
        display(DisplayStyle.Grid)
        property("grid-template-columns", "repeat(auto-fit, minmax(160px, 1fr))")
        gap(10.px)
    }
    val actionButton by style {
        minHeight(56.px)
        borderRadius(8.px)
        border(0.px)
        backgroundColor(Color("#161a22"))
        color(Color.white)
        fontSize(16.px)
        fontWeight(800)
        cursor("pointer")
        property("border", "1px solid rgba(255,255,255,0.10)")
        self + hover style {
            backgroundColor(UISettings.ACCENT)
        }
        self + disabled style {
            opacity(0.42)
            cursor("not-allowed")
        }
    }
    val resetButton by style {
        minHeight(56.px)
        borderRadius(8.px)
        border(0.px)
        backgroundColor(Color("#262b35"))
        color(Color.white)
        fontSize(16.px)
        fontWeight(800)
        cursor("pointer")
        property("border", "1px solid rgba(255,255,255,0.16)")
        self + disabled style {
            opacity(0.42)
            cursor("not-allowed")
        }
    }
    val message by style {
        padding(12.px, 14.px)
        borderRadius(8.px)
        backgroundColor(Color(UISettings.accentRgba(0.12)))
        color(UISettings.ACCENT)
        property("border", "1px solid ${UISettings.accentRgba(0.26)}")
        fontWeight(700)
    }
    val checkFlash by style {
        position(Position.Fixed)
        left(50.percent)
        top(50.percent)
        width(92.px)
        height(92.px)
        borderRadius(50.percent)
        backgroundColor(Color("#17b26a"))
        color(Color.white)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        property("transform", "translate(-50%, -50%)")
        property("z-index", "99999")
        fontSize(34.px)
        fontWeight(900)
        property("box-shadow", "0 18px 42px rgba(0,0,0,0.42)")
    }
    val cameraPreview by style {
        width(100.percent)
        minHeight(34.px)
        backgroundColor(Color.transparent)
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        color(Color("#b8bdc9"))
        fontSize(16.px)
        fontWeight(800)
        textAlign("center")
        overflow("hidden")
        position(Position.Relative)
    }
    val settingsOverlay by style {
        position(Position.Fixed)
        left(0.px)
        top(0.px)
        right(0.px)
        bottom(0.px)
        property("z-index", "100000")
        backgroundColor(rgba(0, 0, 0, 0.82))
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        padding(18.px)
        boxSizing("border-box")
    }
    val settingsBox by style {
        width(420.px)
        maxWidth(100.percent)
        padding(20.px)
        borderRadius(10.px)
        backgroundColor(Color("#17191d"))
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(12.px)
        boxSizing("border-box")
    }
    val settingsTitle by style {
        color(Color.white)
        fontSize(20.px)
        fontWeight(900)
        textAlign("center")
    }
    val settingsInput by style {
        width(100.percent)
        height(48.px)
        padding(0.px, 12.px)
        borderRadius(7.px)
        border(1.px, LineStyle.Solid, rgba(255, 255, 255, 0.16))
        backgroundColor(Color("#090b10"))
        color(Color.white)
        fontSize(16.px)
        boxSizing("border-box")
    }
    val settingsActions by style {
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.FlexEnd)
        gap(8.px)
    }
}
